import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { LogoMark } from "@/components/Logo";
import { AUTH } from "@/constants/testIds";
import { ArrowUpRight, Eye, EyeOff } from "lucide-react";

export default function Login() {
  const { user, loading, login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!loading && user && user !== false) nav("/admin", { replace: true });
  }, [loading, user, nav]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    const r = await login(email, password);
    setBusy(false);
    if (r.ok) nav("/admin");
    else setErr(r.error);
  };

  return (
    <div className="admin-dark min-h-screen bg-ink-950 flex items-center justify-center p-6 relative overflow-hidden">
      {/* ambient ruby wash */}
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute -top-40 -right-40 w-[600px] h-[600px] rounded-full" style={{ background: "radial-gradient(circle, rgba(139,28,49,0.18) 0%, transparent 65%)" }} />
        <div className="absolute -bottom-40 -left-40 w-[600px] h-[600px] rounded-full" style={{ background: "radial-gradient(circle, rgba(139,28,49,0.12) 0%, transparent 65%)" }} />
      </div>

      <div className="relative z-10 w-full max-w-md">
        <div className="flex items-center justify-between mb-12">
          <Link to="/" className="inline-flex items-center gap-3">
            <LogoMark size={28} className="text-cream-100" />
            <span className="font-serif text-2xl text-cream-100">Rubys<span className="text-ruby">.</span>Group</span>
          </Link>
          <Link to="/" className="text-[0.65rem] font-mono uppercase tracking-[0.2em] text-cream-100/40 hover:text-ruby transition-colors">← Site</Link>
        </div>

        <div className="border-t-2 border-ruby pt-10">
          <p className="font-mono uppercase tracking-[0.22em] text-[0.7rem] text-ruby">Operating System</p>
          <h1 className="serif-display text-5xl lg:text-6xl text-cream-100 mt-6">
            Welcome<br /><span className="serif-italic text-ruby">back.</span>
          </h1>
          <p className="text-cream-100/45 mt-6 text-sm">Authorised personnel only.</p>
        </div>

        <form onSubmit={submit} className="mt-12 space-y-9">
          <label className="block">
            <span className="font-mono uppercase tracking-[0.18em] text-[0.65rem] text-cream-100/55">Email</span>
            <input
              type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
              data-testid={AUTH.emailInput} autoComplete="email"
              className="mt-2 w-full bg-transparent border-0 border-b border-cream-100/30 py-3 text-cream-100 text-base tracking-wide focus:outline-none focus:border-ruby transition-colors"
              style={{ color: "#FAF6EE", caretColor: "#B83A52" }}
            />
          </label>
          <label className="block">
            <span className="font-mono uppercase tracking-[0.18em] text-[0.65rem] text-cream-100/55">Password</span>
            <div className="relative mt-2">
              <input
                type={showPw ? "text" : "password"}
                value={password} onChange={(e) => setPassword(e.target.value)} required
                data-testid={AUTH.passwordInput} autoComplete="current-password"
                className="w-full bg-transparent border-0 border-b border-cream-100/30 py-3 pr-10 text-cream-100 text-base tracking-wide focus:outline-none focus:border-ruby transition-colors"
                style={{ color: "#FAF6EE", caretColor: "#B83A52" }}
              />
              <button
                type="button"
                onClick={() => setShowPw((v) => !v)}
                data-testid="auth-toggle-password"
                aria-label={showPw ? "Hide password" : "Show password"}
                className="absolute right-0 top-1/2 -translate-y-1/2 p-2 text-cream-100/50 hover:text-ruby-light transition-colors"
              >
                {showPw ? <EyeOff size={16} strokeWidth={1.4} /> : <Eye size={16} strokeWidth={1.4} />}
              </button>
            </div>
          </label>
          {err && <p className="text-sm text-ruby-light" data-testid={AUTH.error}>{err}</p>}
          <button type="submit" disabled={busy} data-testid={AUTH.submit}
            className="w-full bg-ruby text-cream-100 py-4 uppercase tracking-[0.22em] text-[0.75rem] font-medium hover:bg-ruby-deep transition-colors disabled:opacity-50 inline-flex items-center justify-center gap-3">
            {busy ? "Signing in…" : (<>Sign In <ArrowUpRight size={14} /></>)}
          </button>
        </form>

        <p className="mt-12 text-xs text-cream-100/35">
          Trouble signing in? <a href="mailto:info@rubys.group" className="text-ruby-light hover:text-cream-100 transition-colors">info@rubys.group</a>
        </p>
      </div>
    </div>
  );
}
